import { NotificationCheckIcon } from '@tableau/icons/NotificationCheckIcon';
import {
  ButtonType,
  ButtonWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  TextFieldWidget,
} from '@tableau/widgets';
import React from 'react';

import { showActionableToast } from '~commonInfra/components/ActionableMessage';
import { CreateCustomDialogFn } from '~commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { Messages } from '~src/codegen/Localize';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { IContentRelayPlanInfo } from '../models/IContentRelayPlanInfo';
import { ContentRelayPlansResourceQuery } from '../ResourceQuery/ContentRelayPlansResourceQuery';
import { addSelectionToRelayPlan, createRelayPlanWithSelection } from '../utils/relayPlanActions';
import { buildSelectionQueriesForItems } from '../utils/relaySelection';
import { RelayPlanDialogStyles } from './RelayPlanDialogStyles';

/** How many existing plans to load into the picker. Token paging beyond this is a follow-up. */
const PLAN_FETCH_SIZE = 200;

export interface RelayPlanDialogProps {
  /** The content selected in Explore. */
  items: ReadonlyArray<IContentInfo>;
  onClose: () => void;
}

function siteUrls(): SpacesSiteUrls {
  return new SpacesSiteUrls({ siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName() });
}

function navigateToPlan(luid: string): void {
  WindowLocationService.navigateTo(siteUrls().contentRelayPlanUrl(luid));
}

function showErrorToast(): void {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.relayPlanErrorToast() });
}

/**
 * The dialog behind Explore → Actions → "Add to Relay Plan". A single "Content Relay plan" section
 * both picks an existing plan from a searchable list and, via "+ New plan", names a new one — choosing
 * one clears the other. Creating a plan lands on its detail page; adding to an existing plan stays in
 * Explore and offers "Go to plan" in a toast.
 *
 * Membership is sent as selection queries (one single-id query per selected item). "Add to existing"
 * reads the plan's current queries and sends the merged set, because `updatePlan` replaces the
 * selection wholesale.
 */
export function RelayPlanDialog(props: RelayPlanDialogProps): JSX.Element {
  // Built once from the selection — the selection queries we add on either submit path.
  const [incoming] = React.useState(function() { return buildSelectionQueriesForItems(props.items); });

  const [plans, setPlans] = React.useState<ReadonlyArray<IContentRelayPlanInfo>>([]);
  const [loadingPlans, setLoadingPlans] = React.useState(true);
  // Opened with no selection (e.g. the "New plan" button) ⇒ pure create-a-new-plan mode.
  const newPlanOnly = props.items.length === 0;
  const [isNew, setIsNew] = React.useState(newPlanOnly);
  const [name, setName] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [targetLuid, setTargetLuid] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(function() {
    let cancelled = false;
    new ContentRelayPlansResourceQuery().getSlice(0, PLAN_FETCH_SIZE)
      .then(function(result) {
        if (cancelled) {
          return;
        }
        setPlans(result.items);
        // With no existing plans to pick from, start on the "new plan" path directly.
        setIsNew(newPlanOnly || result.items.length === 0);
        setLoadingPlans(false);
      })
      .catch(function() {
        if (cancelled) {
          return;
        }
        setIsNew(true);
        setLoadingPlans(false);
      });
    return function() { cancelled = true; };
  }, [newPlanOnly]);

  const targetPlan = isNew ? undefined : plans.filter(function(plan) { return plan.luid === targetLuid; })[0];

  const canSubmit = !submitting && (isNew ? !!name.trim() : (!!targetPlan && incoming.length > 0));

  const onSelectPlan = function(luid: string): void {
    setTargetLuid(luid);
    setIsNew(false);
  };

  const onNewPlan = function(): void {
    setTargetLuid('');
    setIsNew(true);
  };

  const onSubmit = function(): void {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);

    if (isNew) {
      const newName = name.trim();
      createRelayPlanWithSelection(newName, incoming)
        .then(function(plan) {
          // Stay on the current content list (like "Add to Collections"); don't navigate away.
          // Offer opening the new plan as an opt-in toast link rather than an auto-redirect.
          props.onClose();
          showActionableToast({
            testId: 'relay-plan-created-toast',
            message: Messages.relayPlanCreatedToast({ PLAN_NAME: newName }),
            actionMessage: Messages.relayPlanGoToPlan(),
            action: function() { navigateToPlan(plan.luid); },
          }, ToastType.Success);
        })
        .catch(function() {
          setSubmitting(false);
          showErrorToast();
        });
      return;
    }

    const luid = targetPlan.luid;
    const planName = targetPlan.name;
    addSelectionToRelayPlan(luid, incoming)
      .then(function(addedCount) {
        props.onClose();
        showActionableToast({
          testId: 'relay-plan-added-toast',
          message: Messages.relayPlanAddedToast({ COUNT: addedCount, PLAN_NAME: planName }),
          actionMessage: Messages.relayPlanGoToPlan(),
          action: function() { navigateToPlan(luid); },
        }, ToastType.Success);
      })
      .catch(function() {
        setSubmitting(false);
        showErrorToast();
      });
  };

  const footer = (
    <DialogFooterButtonsWidget
      buttons={[
        {
          buttonType: ButtonType.Outline,
          onClick: props.onClose,
          children: Messages.relayPlanDialogCancel(),
          testId: 'relay-plan-cancel',
        },
        {
          buttonType: ButtonType.Primary,
          onClick: onSubmit,
          disabled: !canSubmit,
          children: isNew ? Messages.relayPlanDialogCreate() : Messages.relayPlanDialogAdd(),
          testId: 'relay-plan-confirm',
        },
      ]}
    />
  );

  return (
    <DialogWidget
      type={DialogType.NormalWithTitleBar}
      title={newPlanOnly ? Messages.relayPlanDialogCreateTitle() : Messages.relayPlanDialogTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      positioningBehavior={DialogPositioningBehavior.Centered}
      isModal={true}
      dialogWidth={600}
      footerContent={footer}
      testId={'relay-plan-dialog'}
    >
      <PlanPicker
        plans={plans}
        loading={loadingPlans}
        search={search}
        onSearch={setSearch}
        selectedLuid={isNew ? '' : targetLuid}
        onSelect={onSelectPlan}
        isNew={isNew}
        onNewPlan={onNewPlan}
        name={name}
        onNameChange={setName}
        newPlanOnly={newPlanOnly}
      />
    </DialogWidget>
  );
}

interface PlanPickerProps {
  plans: ReadonlyArray<IContentRelayPlanInfo>;
  loading: boolean;
  search: string;
  onSearch: (search: string) => void;
  selectedLuid: string;
  onSelect: (luid: string) => void;
  isNew: boolean;
  onNewPlan: () => void;
  name: string;
  onNameChange: (name: string) => void;
  newPlanOnly: boolean;
}

/**
 * The "Content Relay plan" section: a name search over a single-select list of existing plans, then
 * "+ New plan" under the list, which deselects the list and swaps in a name field for the new plan.
 */
function PlanPicker(props: PlanPickerProps): JSX.Element {
  const needle = props.search.trim().toLowerCase();
  const matches = props.plans.filter(function(plan) {
    return plan.name.toLowerCase().indexOf(needle) !== -1;
  });
  const hasPlans = props.plans.length > 0;

  return (
    <div>
      <div className={RelayPlanDialogStyles.sectionTitle}>{Messages.relayPlanDialogPlanHeading()}</div>

      {hasPlans && !props.newPlanOnly ? (
        <div>
          <TextFieldWidget
            className={RelayPlanDialogStyles.fullWidthField}
            kind={'search'}
            label={''}
            aria-label={Messages.relayPlanDialogSearchPlaceholder()}
            placeholder={Messages.relayPlanDialogSearchPlaceholder()}
            value={props.search}
            onChange={function(e) { props.onSearch(e.target.value); }}
            onClear={function() { props.onSearch(''); }}
            testId={'relay-plan-search'}
          />
          <div className={RelayPlanDialogStyles.planList} role={'listbox'} aria-label={Messages.relayPlanDialogPlanHeading()}>
            {matches.length ? matches.map(function(plan) {
              const selected = plan.luid === props.selectedLuid;
              return (
                <button
                  key={plan.luid}
                  type={'button'}
                  role={'option'}
                  aria-selected={selected}
                  className={RelayPlanDialogStyles.planOption}
                  onClick={function() { props.onSelect(plan.luid); }}
                  data-tb-test-id={'relay-plan-option'}
                >
                  <span className={RelayPlanDialogStyles.planOptionCheck} aria-hidden={true}>
                    {selected ? <NotificationCheckIcon size={12} /> : null}
                  </span>
                  <span className={RelayPlanDialogStyles.planOptionName}>{plan.name}</span>
                </button>
              );
            }) : (
              <div className={RelayPlanDialogStyles.planListEmpty}>{Messages.relayPlanDialogNoPlansMatch()}</div>
            )}
          </div>
        </div>
      ) : null}

      <div className={hasPlans && !props.newPlanOnly ? RelayPlanDialogStyles.newPlanRow : undefined}>
        {props.isNew ? (
          <TextFieldWidget
            className={RelayPlanDialogStyles.fullWidthField}
            label={Messages.relayPlanDialogNameLabel()}
            placeholder={Messages.relayPlanDialogNamePlaceholder()}
            value={props.name}
            onChange={function(e) { props.onNameChange(e.target.value); }}
            testId={'relay-plan-name'}
          />
        ) : props.loading ? null : (
          <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={props.onNewPlan} testId={'relay-plan-new'}>
            {Messages.relayPlanDialogNewPlan()}
          </ButtonWidget>
        )}
      </div>
    </div>
  );
}

/** Options for ReactModalService.openCustom. */
export function RelayPlanDialogOpts(items: ReadonlyArray<IContentInfo>): CreateCustomDialogFn {
  return function(closeAndDismiss) {
    return <RelayPlanDialog items={items} onClose={closeAndDismiss.close} />;
  };
}
